import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  IoAddOutline,
  IoBagHandleOutline,
  IoCloseOutline,
  IoHeart,
  IoHeartOutline,
  IoMenuOutline,
  IoRemoveOutline,
  IoSearchOutline,
  IoTrashOutline,
} from "react-icons/io5";
import siteConfig, { formatPrice } from "../config/siteConfig";
import productData from "../Datas/productData";
import "../css/header.scss";
import { CartContext } from "./CartContext";

const navigation = [
  { label: "Shop", to: "/shop" },
  { label: "Paper Vases", to: "/shop?category=Paper%20Vases" },
  { label: "Tableware", to: "/shop?category=Tableware" },
  { label: "Home & Living", to: "/shop?category=Home%20Decor" },
  { label: "Our Story", to: "/about" },
];

const shopCategories = [...new Set(productData.map((product) => product.category))];

const safeScrollToTop = () => {
  if (
    typeof window === "undefined" ||
    typeof window.scrollTo !== "function" ||
    /jsdom/i.test(window.navigator?.userAgent || "")
  ) {
    return;
  }

  try {
    window.scrollTo({ top: 0, behavior: "auto" });
  } catch {
    // Some embedded browsers expose scrollTo without implementing it.
  }
};

function Header() {
  const {
    cartItems,
    cartCount,
    removeFromCart,
    updateQuantity,
    wishlistItems,
    removeFromWishlist,
  } = useContext(CartContext);
  const location = useLocation();
  const searchInputRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [homeHeaderScrolled, setHomeHeaderScrolled] = useState(false);
  const isHomePage = location.pathname === "/";

  const detailedCartItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find(
            (product) => product.id === Number(item.id)
          ),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const wishlistProducts = useMemo(
    () =>
      wishlistItems
        .map((id) => productData.find((product) => product.id === Number(id)))
        .filter(Boolean),
    [wishlistItems]
  );

  const subtotal = useMemo(
    () =>
      detailedCartItems.reduce(
        (total, item) => total + Number(item.product.price) * item.quantity,
        0
      ),
    [detailedCartItems]
  );

  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  const searchResults = useMemo(() => {
    if (!normalizedQuery) {
      return productData.filter((product) => product.featured).slice(0, 8);
    }

    const queryTerms = normalizedQuery.split(/\s+/).filter(Boolean);
    return productData.filter((product) => {
      const searchText = [
        product.name,
        product.category,
        ...(product.tags || []),
      ]
        .join(" ")
        .toLocaleLowerCase();

      return queryTerms.every((term) => searchText.includes(term));
    });
  }, [normalizedQuery]);

  const closeAllPanels = () => {
    setSearchOpen(false);
    setMobileMenuOpen(false);
    setActiveDrawer(null);
  };

  const openSearch = () => {
    setMobileMenuOpen(false);
    setActiveDrawer(null);
    setSearchOpen(true);
  };

  const openDrawer = (drawer) => {
    setSearchOpen(false);
    setMobileMenuOpen(false);
    setActiveDrawer(drawer);
  };

  const isNavigationItemActive = (item) => {
    if (item.to.includes("?")) {
      return `${location.pathname}${location.search}` === item.to;
    }
    return (
      location.pathname === item.to &&
      (item.to !== "/shop" || !location.search)
    );
  };

  useEffect(() => {
    closeAllPanels();
    safeScrollToTop();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isHomePage) {
      setHomeHeaderScrolled(false);
      return undefined;
    }

    const updateHeaderSurface = () => {
      setHomeHeaderScrolled(window.scrollY > 24);
    };

    updateHeaderSurface();
    window.addEventListener("scroll", updateHeaderSurface, { passive: true });
    return () => window.removeEventListener("scroll", updateHeaderSurface);
  }, [isHomePage]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeAllPanels();
    };
    const handleCartOpen = () => openDrawer("cart");

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("ottireluna:cart-open", handleCartOpen);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("ottireluna:cart-open", handleCartOpen);
    };
  }, []);

  useEffect(() => {
    const panelIsOpen = searchOpen || mobileMenuOpen || Boolean(activeDrawer);
    if (!panelIsOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeDrawer, mobileMenuOpen, searchOpen]);


  return (
    <>
      <header
        className={`site-header${isHomePage ? " site-header--home" : ""}${
          homeHeaderScrolled ? " is-scrolled" : ""
        }`}
      >
        <div className="site-header__announcement">
          <Link to="/shipping">{siteConfig.announcement}</Link>
        </div>

        <div className="site-header__bar">
          <button
            className="site-header__mobile-menu-button"
            type="button"
            aria-label="Open menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            onClick={() => {
              setSearchOpen(false);
              setActiveDrawer(null);
              setMobileMenuOpen(true);
            }}
          >
            <IoMenuOutline aria-hidden="true" />
          </button>

          <Link
            to="/"
            className="site-header__wordmark"
            aria-label={siteConfig.logo.ariaLabel}
          >
            {siteConfig.logo.text}
          </Link>

          <nav className="site-header__navigation" aria-label="Main navigation">
            {navigation.map((item) =>
              item.to === "/shop" ? (
                <div className="site-header__nav-item--mega" key={item.label}>
                  <Link
                    to={item.to}
                    className={`site-header__nav-trigger${
                      location.pathname.startsWith("/shop") ? " is-active" : ""
                    }`}
                    aria-haspopup="true"
                    aria-controls="shop-mega-panel"
                  >
                    {item.label}
                  </Link>

                  <div
                    className="site-header__mega-panel"
                    id="shop-mega-panel"
                    aria-label="Shop collections"
                  >
                    <div className="site-header__mega-intro">
                      <span>The collection</span>
                      <h2>Bring warmth home.</h2>
                      <p>
                        Joyful objects shaped by craft, colour and long days in
                        the sun.
                      </p>
                      <Link to="/shop">View all objects <span aria-hidden="true">→</span></Link>
                    </div>

                    <div className="site-header__mega-categories">
                      <p>Shop by category</p>
                      {shopCategories.map((category) => (
                        <Link
                          key={category}
                          to={`/shop?category=${encodeURIComponent(category)}`}
                        >
                          <span>{category}</span>
                          <small>
                            {
                              productData.filter(
                                (product) => product.category === category
                              ).length
                            }
                          </small>
                        </Link>
                      ))}
                    </div>

                    <div className="site-header__mega-editorials">
                      <Link to="/shop?category=Paper%20Vases">
                        <div>
                          <img
                            src="/main/paper-vases-editorial.jpg"
                            alt="Colourful sculptural paper vases in a sunlit interior"
                          />
                        </div>
                        <span>Paper Vases</span>
                        <strong>Flowers, reimagined</strong>
                      </Link>
                      <Link to="/shop?category=Tableware">
                        <div>
                          <img
                            src="/main/social-table.jpg"
                            alt="A colourful table set for sharing"
                          />
                        </div>
                        <span>Tableware</span>
                        <strong>Set a social table</strong>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className={
                    isNavigationItemActive(item) ? "is-active" : undefined
                  }
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="site-header__more">
              <button type="button" aria-haspopup="true">More</button>
              <div className="site-header__more-menu">
                <Link to="/faqs">FAQs</Link><Link to="/shipping">Shipping</Link>
                <Link to="/returns">Returns</Link><Link to="/privacy">Privacy</Link>
                <Link to="/cookies">Cookies</Link><Link to="/terms">Terms</Link>
              </div>
            </div>
          </nav>

          <div className="site-header__actions">
            <button type="button" onClick={openSearch} aria-label="Search">
              <IoSearchOutline aria-hidden="true" />
              <span className="site-header__action-label">Search</span>
            </button>
            <button
              className="site-header__wishlist-action"
              type="button"
              onClick={() => openDrawer("wishlist")}
              aria-label={`Wishlist, ${wishlistItems.length} items`}
            >
              {wishlistItems.length > 0 ? (
                <IoHeart aria-hidden="true" />
              ) : (
                <IoHeartOutline aria-hidden="true" />
              )}
              <span className="site-header__action-label">Saved</span>
              {wishlistItems.length > 0 && (
                <span className="site-header__badge" aria-hidden="true">
                  {wishlistItems.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => openDrawer("cart")}
              aria-label={`Shopping bag, ${cartCount} items`}
            >
              <IoBagHandleOutline aria-hidden="true" />
              <span className="site-header__action-label">Bag</span>
              {cartCount > 0 && (
                <span className="site-header__badge" aria-hidden="true">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <>
          <button
            className="site-header__backdrop"
            type="button"
            aria-label="Close menu"
            onClick={closeAllPanels}
          />
          <aside
            id="mobile-navigation"
            className="mobile-navigation"
            aria-label="Mobile navigation"
          >
            <div className="mobile-navigation__head">
              <span>{siteConfig.logo.text}</span>
              <button type="button" onClick={closeAllPanels} aria-label="Close menu">
                <IoCloseOutline aria-hidden="true" />
              </button>
            </div>
            <nav>
              {[...navigation, { label: "FAQs", to: "/faqs" }, { label: "Privacy", to: "/privacy" }, { label: "Cookies", to: "/cookies" }].map((item, index) => (
                <Link key={item.label} to={item.to}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="mobile-navigation__utility">
              <button type="button" onClick={openSearch}>
                <IoSearchOutline aria-hidden="true" /> Search the collection
              </button>
              <button type="button" onClick={() => openDrawer("wishlist")}>
                <IoHeartOutline aria-hidden="true" /> Saved pieces (
                {wishlistItems.length})
              </button>
            </div>
            <p>{siteConfig.location}</p>
          </aside>
        </>
      )}

      {searchOpen && (
        <section
          className="site-search"
          role="dialog"
          aria-modal="true"
          aria-labelledby="site-search-title"
        >
          <div className="site-search__topbar">
            <p id="site-search-title">Search the collection</p>
            <button type="button" onClick={closeAllPanels} aria-label="Close search">
              <IoCloseOutline aria-hidden="true" />
            </button>
          </div>
          <div className="site-search__inner">
            <label className="site-search__field">
              <IoSearchOutline aria-hidden="true" />
              <span className="visually-hidden">Search products</span>
              <input
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                placeholder="What are you looking for?"
                autoComplete="off"
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  Clear
                </button>
              )}
            </label>

            {!normalizedQuery && (
              <div className="site-search__suggestions" aria-label="Popular searches">
                <span>Try</span>
                {["Paper Vases", "Ceramic", "Gift", "Tableware"].map(
                  (suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setSearchQuery(suggestion)}
                    >
                      {suggestion}
                    </button>
                  )
                )}
              </div>
            )}

            <div className="site-search__results-head" aria-live="polite">
              <h2>{normalizedQuery ? "Search results" : "Selected objects"}</h2>
              <span>
                {searchResults.length} {searchResults.length === 1 ? "item" : "items"}
              </span>
            </div>

            {searchResults.length > 0 ? (
              <div className="site-search__grid">
                {searchResults.slice(0, 12).map((product) => (
                  <Link
                    key={product.id}
                    to={`/shopDetails/${product.id}`}
                    className="site-search__product"
                  >
                    <div className="site-search__image-wrap">
                      <img src={product.image} alt="" />
                    </div>
                    <div>
                      <span>{product.category}</span>
                      <h3>{product.name}</h3>
                      <p>{formatPrice(product.price)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="site-search__empty">
                <p>No objects match “{searchQuery}”.</p>
                <button type="button" onClick={() => setSearchQuery("")}>
                  Clear search
                </button>
                <Link to="/shop">View all objects</Link>
              </div>
            )}
          </div>
        </section>
      )}

      {activeDrawer && (
        <>
          <button
            className="site-header__backdrop"
            type="button"
            aria-label={`Close ${activeDrawer}`}
            onClick={closeAllPanels}
          />
          <aside
            className="header-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="header-drawer-title"
          >
            <div className="header-drawer__head">
              <div>
                <span>{activeDrawer === "cart" ? "Your selection" : "Keep for later"}</span>
                <h2 id="header-drawer-title">
                  {activeDrawer === "cart" ? "Shopping bag" : "Saved objects"}
                </h2>
              </div>
              <button type="button" onClick={closeAllPanels} aria-label="Close panel">
                <IoCloseOutline aria-hidden="true" />
              </button>
            </div>

            {activeDrawer === "cart" && (
              <>
                {detailedCartItems.length > 0 ? (
                  <>
                    <div className="header-drawer__shipping">
                      <p>
                        Tracked delivery is {formatPrice(siteConfig.shipping.standardRate)}
                      </p>
                      <div aria-hidden="true">
                        <span style={{ width: "100%" }} />
                      </div>
                    </div>
                    <div className="header-drawer__items">
                      {detailedCartItems.map((item) => (
                        <article
                          className="header-drawer__item"
                          key={`${item.id}-${item.size}-${item.color}`}
                        >
                          <Link
                            to={`/shopDetails/${item.product.id}`}
                            className="header-drawer__item-image"
                          >
                            <img src={item.product.image} alt="" />
                          </Link>
                          <div className="header-drawer__item-copy">
                            <span>{item.product.category}</span>
                            <Link to={`/shopDetails/${item.product.id}`}>
                              {item.product.name}
                            </Link>
                            {(item.color || item.size) && (
                              <p>
                                {[item.color, item.size].filter(Boolean).join(" · ")}
                              </p>
                            )}
                            <div className="header-drawer__quantity">
                              <button
                                type="button"
                                aria-label={`Decrease ${item.product.name} quantity`}
                                onClick={() =>
                                  item.quantity === 1
                                    ? removeFromCart(item.id, item.size, item.color)
                                    : updateQuantity(
                                        item.id,
                                        item.size,
                                        item.color,
                                        item.quantity - 1
                                      )
                                }
                              >
                                <IoRemoveOutline aria-hidden="true" />
                              </button>
                              <span aria-label={`Quantity ${item.quantity}`}>
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                aria-label={`Increase ${item.product.name} quantity`}
                                onClick={() =>
                                  updateQuantity(
                                    item.id,
                                    item.size,
                                    item.color,
                                    item.quantity + 1
                                  )
                                }
                              >
                                <IoAddOutline aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                          <div className="header-drawer__item-end">
                            <strong>
                              {formatPrice(item.product.price * item.quantity)}
                            </strong>
                            <button
                              type="button"
                              aria-label={`Remove ${item.product.name} from bag`}
                              onClick={() =>
                                removeFromCart(item.id, item.size, item.color)
                              }
                            >
                              <IoTrashOutline aria-hidden="true" />
                            </button>
                          </div>
                        </article>
                      ))}
                    </div>
                    <div className="header-drawer__footer">
                      <div className="header-drawer__subtotal">
                        <span>Subtotal</span>
                        <strong>{formatPrice(subtotal)}</strong>
                      </div>
                      <p>Taxes included. Shipping calculated at checkout.</p>
                      <Link className="header-drawer__primary" to="/checkout">
                        Checkout
                      </Link>
                      <Link className="header-drawer__secondary" to="/cartpage">
                        View shopping bag
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="header-drawer__empty">
                    <IoBagHandleOutline aria-hidden="true" />
                    <h3>Your bag is waiting for some sunshine.</h3>
                    <p>Explore colourful objects for tables, shelves and desks.</p>
                    <Link to="/shop">Discover the collection</Link>
                  </div>
                )}
              </>
            )}

            {activeDrawer === "wishlist" && (
              <>
                {wishlistProducts.length > 0 ? (
                  <div className="header-drawer__items header-drawer__items--wishlist">
                    {wishlistProducts.map((product) => (
                      <article className="header-drawer__item" key={product.id}>
                        <Link
                          to={`/shopDetails/${product.id}`}
                          className="header-drawer__item-image"
                        >
                          <img src={product.image} alt="" />
                        </Link>
                        <div className="header-drawer__item-copy">
                          <span>{product.category}</span>
                          <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                          <strong>{formatPrice(product.price)}</strong>
                        </div>
                        <div className="header-drawer__item-end">
                          <button
                            type="button"
                            aria-label={`Remove ${product.name} from saved objects`}
                            onClick={() => removeFromWishlist(product.id)}
                          >
                            <IoCloseOutline aria-hidden="true" />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="header-drawer__empty">
                    <IoHeartOutline aria-hidden="true" />
                    <h3>Save the objects you love.</h3>
                    <p>Tap the heart on any product and find it here later.</p>
                    <Link to="/shop">Explore all objects</Link>
                  </div>
                )}
              </>
            )}
          </aside>
        </>
      )}
    </>
  );
}

export default Header;
